/**
 * Helpers para formatear fechas/horas en la app.
 *
 * IMPORTANTE — por qué TODO se formatea con `timeZone: 'UTC'`:
 *
 * Los formularios usan `<input type="datetime-local">` que da un string
 * "naive" sin timezone (ej. `"2026-04-30T19:00"`). Cuando se guarda en
 * Postgres (columna `timestamptz`), el server lo interpreta como UTC por
 * defecto y queda almacenado como `2026-04-30 19:00:00+00`.
 *
 * Si después al mostrar usamos `new Date(iso).getHours()`, JavaScript
 * convierte ese UTC a la zona local del navegador → restando el offset
 * → la hora mostrada NO es la que se tipeó. El alumno ve `15:00` cuando
 * el docente escribió `19:00`.
 *
 * Para que "what you typed is what you see" sea consistente para todos,
 * leemos siempre en UTC. Así, "19:00 UTC" se muestra como "19:00" sin
 * importar la TZ del navegador.
 */

const LOCALE = 'es-ES';
const TZ     = 'UTC' as const;

/** Devuelve `19:00`. Para módulos/clases/eventos del intranet. */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(LOCALE, {
      hour:     '2-digit',
      minute:   '2-digit',
      hour12:   false,
      timeZone: TZ,
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

/** Devuelve `30 de abril de 2026`. */
export function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(LOCALE, {
      day:      'numeric',
      month:    'long',
      year:     'numeric',
      timeZone: TZ,
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

/** Devuelve `30/04/2026`. */
export function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(LOCALE, {
      day:      '2-digit',
      month:    '2-digit',
      year:     'numeric',
      timeZone: TZ,
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

/** Devuelve `19:00 - 22:00`, o `—` si falta una de las dos puntas. */
export function fmtTimeRange(
  startIso: string | null | undefined,
  endIso:   string | null | undefined,
): string {
  if (!startIso || !endIso) return 'Por definir';
  return `${fmtTime(startIso)} - ${fmtTime(endIso)}`;
}
